import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Skeleton,
  Alert,
  Collapse,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  AccessTime,
  CheckCircle,
  Info as InfoIcon
} from '@mui/icons-material';
import { formatPrice } from '../../services/bookingService';

// Info text for specific services
const SERVICE_INFO = {
  'hormone replacement therapy initial consultation': {
    title: 'About This Consultation',
    content: `This online call is with one of our staff, they will explain your hormone replacement therapy journey. After you complete an online form and upload recent blood test results, a doctor's consultation will be scheduled to discuss your treatment and make recommendations.

You'll then receive a link to a customer portal to enable you to order any of the recommended prescription (if appropriate) and non prescription products. Orders are shipped directly to you.

Blood tests are required every 12 weeks for doctor assessment if on a hormone program, follow up consultations with the doctor are $259.`
  },
  'longevity medicine (peptide therapy) consultation': {
    title: 'About This Consultation',
    content: `The initial online call is with one of our staff, they will explain your longevity medicine and peptide therapy journey. After you complete an online form, and speak to one of our staff, a doctor's consultation will be scheduled to discuss your treatment and make recommendations.

You'll receive a link to a customer portal to enable you to order any of the recommended prescription (if appropriate) and non prescription products.

Orders are shipped directly to you, from one of our Australian accredited partner pharmacies.

Important: After booking, please check your inbox for an email with a link to complete our Integrative Health Review Form.`
  },
  'weight loss consultation': {
    title: 'About This Consultation',
    content: `The initial online call is with one of our staff, they will explain your weight loss program and journey. After you complete an online form, and speak to one of our staff, a doctor's consultation will be scheduled to discuss your treatment and make recommendations.

You'll receive a link to a customer portal to enable you to order any of the recommended prescription (if appropriate) and non prescription products.

Orders are shipped directly to you, from one of our Australian accredited partner pharmacies.

Important: Our online intake form must be completed prior to your doctor consultation - this will be sent to you via email.`
  },
  'personalised nutrition plan': {
    title: 'About This Consultation',
    content: `The initial online call is with one of our staff, they will explain your weight loss program and journey. After you complete an online form, and speak to one of our staff, a doctor's consultation will be scheduled to discuss your treatment and make recommendations.

You'll receive a link to a customer portal to enable you to order any of the recommended prescription (if appropriate) and non prescription products.

Orders are shipped directly to you, from one of our Australian accredited partner pharmacies.

Important: Our online intake form must be completed prior to your doctor consultation - this will be sent to you via email.`
  },
  'integrative health plan': {
    title: 'About This Consultation',
    content: `One of our team will contact you to go through the process in detail.

Our online intake form must be completed prior to your doctor consultation - this will be sent to you via email.`
  }
};

// Helper function to check if a service should show info dialog
const getServiceInfo = (serviceName) => {
  if (!serviceName) return null;
  const normalizedName = serviceName.toLowerCase().trim();
  return SERVICE_INFO[normalizedName] || null;
};

const MobileServiceSelection = ({
  services = [],
  selectedService,
  onSelect,
  loading = false,
  displayMode = 'flat',
  showInfoForServiceId = null,  // If set, automatically show info dialog for this service
  filterServiceIds = null  // If set, only show services with these IDs (array of strings)
}) => {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [pendingService, setPendingService] = useState(null);
  const [serviceInfo, setServiceInfo] = useState(null);
  const [autoShowTriggered, setAutoShowTriggered] = useState(false);

  // Auto-show info dialog when showInfoForServiceId is provided
  React.useEffect(() => {
    if (showInfoForServiceId && services.length > 0 && !autoShowTriggered) {
      const targetService = services.find(
        s => s.id.toString() === showInfoForServiceId.toString()
      );
      if (targetService) {
        const info = getServiceInfo(targetService.name);
        if (info) {
          // Show info dialog for this service
          setPendingService(targetService);
          setServiceInfo(info);
          setInfoDialogOpen(true);
          setAutoShowTriggered(true);
        } else {
          // No info dialog needed, select directly
          onSelect(targetService);
          setAutoShowTriggered(true);
        }
      }
    }
  }, [showInfoForServiceId, services, autoShowTriggered, onSelect]);

  // Handle service click - check if info dialog should be shown
  const handleServiceClick = (service) => {
    const info = getServiceInfo(service.name);
    if (info) {
      // Show info dialog before proceeding
      setPendingService(service);
      setServiceInfo(info);
      setInfoDialogOpen(true);
    } else {
      // No info dialog needed, proceed directly
      onSelect(service);
    }
  };

  // Handle continue after reading info
  const handleContinue = () => {
    setInfoDialogOpen(false);
    if (pendingService) {
      onSelect(pendingService);
      setPendingService(null);
      setServiceInfo(null);
    }
  };

  // Handle dialog close without continuing
  const handleCloseDialog = () => {
    setInfoDialogOpen(false);
    setPendingService(null);
    setServiceInfo(null);
  };

  // Loading state
  if (loading) {
    return (
      <Box>
        <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
          Select a Service
        </Typography>
        {[1, 2, 3, 4].map(i => (
          <Paper key={i} sx={{ mb: 1.5, p: 2 }}>
            <Skeleton variant="text" width="60%" height={28} />
            <Skeleton variant="text" width="40%" height={20} sx={{ mt: 0.5 }} />
          </Paper>
        ))}
      </Box>
    );
  }

  // No services
  if (!services || services.length === 0) {
    return (
      <Alert severity="info">
        No services available. Please contact the clinic directly.
      </Alert>
    );
  }

  // Filter services based on URL parameters
  let displayServices = services;
  if (filterServiceIds && filterServiceIds.length > 0) {
    // Filter to multiple specific services
    displayServices = services.filter(s => filterServiceIds.includes(s.id.toString()));
  } else if (showInfoForServiceId) {
    // Filter to single service
    displayServices = services.filter(s => s.id.toString() === showInfoForServiceId.toString());
  }

  // Group services by category if grouped mode
  const groupedServices = {};
  if (displayMode === 'grouped') {
    displayServices.forEach(service => {
      const category = service.category_name || service.category || 'Other Services';
      if (!groupedServices[category]) {
        groupedServices[category] = [];
      }
      groupedServices[category].push(service);
    });
  }

  // Render a service card
  const ServiceCard = ({ service }) => {
    const isSelected = selectedService?.id === service.id;

    return (
      <Paper
        onClick={() => handleServiceClick(service)}
        sx={{
          p: 2,
          mb: 1.5,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          border: isSelected ? '2px solid' : '1px solid',
          borderColor: isSelected ? 'primary.main' : 'divider',
          bgcolor: isSelected ? 'primary.light' : 'white',
          '&:hover': {
            borderColor: 'primary.main',
            transform: 'translateY(-2px)',
            boxShadow: 2
          },
          '&:active': {
            transform: 'scale(0.98)'
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, pr: 2 }}>
            <Typography
              variant="subtitle1"
              fontWeight="600"
              sx={{
                color: isSelected ? 'primary.dark' : 'text.primary',
                mb: 0.5
              }}
            >
              {service.name}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {service.duration_in_minutes} min
              </Typography>
            </Box>
          </Box>

          <Box sx={{ textAlign: 'right' }}>
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary.main"
            >
              {formatPrice(service.price)}
            </Typography>
            {isSelected && (
              <CheckCircle
                sx={{ color: 'primary.main', fontSize: 20, mt: 0.5 }}
              />
            )}
          </Box>
        </Box>
      </Paper>
    );
  };

  // Flat display mode
  if (displayMode === 'flat') {
    return (
      <>
        <Box>
          <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
            {showInfoForServiceId ? 'Your Selected Service' : 'Select a Service'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {showInfoForServiceId ? 'Review the service details below' : 'Choose the service you\'d like to book'}
          </Typography>

          {displayServices.map(service => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </Box>

        {/* Info Dialog for specific services */}
        <Dialog
          open={infoDialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            {serviceInfo?.title || 'Important Information'}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ whiteSpace: 'pre-line' }}>
              {serviceInfo?.content}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="inherit">
              Go Back
            </Button>
            <Button onClick={handleContinue} variant="contained" color="primary">
              I Understand, Continue
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Grouped display mode
  return (
    <>
      <Box>
        <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
          Select a Service
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose a category, then select your service
        </Typography>

        {Object.entries(groupedServices).map(([category, categoryServices]) => {
          const isExpanded = expandedCategory === category;
          const hasSelected = categoryServices.some(s => s.id === selectedService?.id);

          return (
            <Paper
              key={category}
              sx={{
                mb: 2,
                overflow: 'hidden',
                border: hasSelected ? '2px solid' : '1px solid',
                borderColor: hasSelected ? 'primary.main' : 'divider'
              }}
            >
              {/* Category Header */}
              <Box
                onClick={() => setExpandedCategory(isExpanded ? null : category)}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  cursor: 'pointer',
                  bgcolor: isExpanded ? 'primary.main' : 'grey.50',
                  color: isExpanded ? 'white' : 'text.primary',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: isExpanded ? 'primary.dark' : 'grey.100'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight="600">
                    {category}
                  </Typography>
                  <Chip
                    label={`${categoryServices.length} ${categoryServices.length === 1 ? 'service' : 'services'}`}
                    size="small"
                    sx={{
                      bgcolor: isExpanded ? 'rgba(255,255,255,0.2)' : 'primary.light',
                      color: isExpanded ? 'white' : 'primary.main',
                      height: 22,
                      fontSize: '0.7rem'
                    }}
                  />
                </Box>
                <IconButton
                  size="small"
                  sx={{ color: isExpanded ? 'white' : 'text.secondary' }}
                >
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>

              {/* Services List */}
              <Collapse in={isExpanded}>
                <Box sx={{ p: 2 }}>
                  {categoryServices.map(service => (
                    <ServiceCard key={service.id} service={service} />
                  ))}
                </Box>
              </Collapse>
            </Paper>
          );
        })}
      </Box>

      {/* Info Dialog for specific services */}
      <Dialog
        open={infoDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="primary" />
          {serviceInfo?.title || 'Important Information'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: 'pre-line' }}>
            {serviceInfo?.content}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Go Back
          </Button>
          <Button onClick={handleContinue} variant="contained" color="primary">
            I Understand, Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MobileServiceSelection;
